/**
 * Document authoring — the wire contract, shared by every surface.
 *
 * These shapes are the SAME objects `backend/app/services/dms/authoring.py`
 * normalises, in the same snake_case, deliberately untranslated. A camelCase
 * mirror would be a second definition of the template, and the moment the two
 * drift an author's document reflows on save.
 *
 * The template is the centre of it. It says what a document of this type looks
 * like — its page, its fonts, the rule for every element, its document control
 * section, and the sections a new document opens with — and it is read by three
 * things that must agree exactly: the editor's CSS, the layout engine that
 * decides where pages break, and the PDF exporter. One template, three
 * consumers, no third opinion.
 */

import type { JSONContent } from '@tiptap/core'

/** The element vocabulary a business document is made of. */
export type ElementId =
  | 'title' | 'subtitle'
  | 'heading1' | 'heading2' | 'heading3' | 'heading4'
  | 'body' | 'listBullet' | 'listNumber' | 'quote' | 'code' | 'caption'
  | 'tableHeader' | 'tableCell'
  | 'controlLabel' | 'controlValue' | 'footnote'

export const ELEMENT_IDS: ElementId[] = [
  'title', 'subtitle', 'heading1', 'heading2', 'heading3', 'heading4',
  'body', 'listBullet', 'listNumber', 'quote', 'code', 'caption',
  'tableHeader', 'tableCell', 'controlLabel', 'controlValue', 'footnote',
]

export const ELEMENT_LABELS: Record<string, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  heading1: 'Heading 1',
  heading2: 'Heading 2',
  heading3: 'Heading 3',
  heading4: 'Heading 4',
  body: 'Body text',
  listBullet: 'Bulleted list',
  listNumber: 'Numbered list',
  quote: 'Quotation',
  code: 'Code / monospaced',
  caption: 'Caption',
  tableHeader: 'Table header',
  tableCell: 'Table cell',
  controlLabel: 'Control section — label',
  controlValue: 'Control section — value',
  footnote: 'Footnote',
}

/**
 * Elements a user may turn a paragraph INTO. The rest take a template rule but
 * are structural — "make this paragraph a table header" is not a thing.
 */
export const STYLE_MENU: ElementId[] = [
  'title', 'subtitle', 'heading1', 'heading2', 'heading3', 'heading4',
  'body', 'quote', 'code', 'caption', 'footnote',
]

export type TextAlign = 'left' | 'center' | 'right' | 'justify'
export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize'

/** Formatting for one element. Every field explicit — see the Python twin. */
export interface FormattingRule {
  id: string
  label: string
  enabled: boolean
  font_family: string | null
  font_size: number | null
  bold: boolean
  italic: boolean
  underline: boolean
  strikethrough: boolean
  text_transform: TextTransform
  text_color: string | null
  background_color: string | null
  text_align: TextAlign
  /** Points of space before / after the block. */
  space_before: number
  space_after: number
  /** Multiplier on the font size. */
  line_height: number
  /** Inches, measured in from the content column's own edges. */
  left_indent: number
  right_indent: number
  first_line_indent: number
  /** Keep this block on the same page as the one after it. */
  keep_with_next: boolean
  page_break_before: boolean
  /** In `enforce` mode, whether a user may still override this element. */
  allow_format_override: boolean
}

export interface HeaderFooterBand {
  left: string
  center: string
  right: string
}

export interface PageLayout {
  /** Inches. */
  page_width: number
  page_height: number
  /** Points. */
  top_margin: number
  bottom_margin: number
  /** Inches — the content column's distance from each page edge. */
  left_margin: number
  right_margin: number
  /** Points from the page edge to the header / footer baseline band. */
  header_margin: number
  footer_margin: number
  header: HeaderFooterBand
  footer: HeaderFooterBand
  /**
   * A header that is document CONTENT, drawn on every page in place of the
   * three-slot band above it.
   *
   * A controlled record's header is a boxed grid — department, product, batch
   * record number, prepared by, approved by, revision, effective date — and
   * three strings cannot be that. Absent on every document that has not asked
   * for one, which is the reason the band keeps working untouched.
   */
  header_block?: JSONContent | null
  footer_block?: JSONContent | null
  header_start_page: number
  footer_start_page: number
  starting_page_number: number
}

export type ControlSource = 'metadata' | 'document' | 'static' | 'author'

export interface ControlRow {
  label: string
  source: ControlSource
  key: string
  value: string
}

/** A control row with its value resolved against the record. */
export interface ResolvedControlRow extends ControlRow {
  /** True when the reader is outside a secure field's reveal roles. */
  masked: boolean
  /** True only for `author` rows — everything else comes from the record. */
  editable: boolean
}

export interface RevisionRow {
  /** The version's id — the compare picker needs it, the printed table does not. */
  id: string
  version: string
  date: string
  author: string
  state: string
}

/**
 * Every version of the document, and whether each one can be compared
 * STRUCTURALLY. Separate from `revision_history` because that is document
 * CONTENT a template may switch off, while the picker must still list versions.
 */
export interface VersionChoice {
  id: string
  label: string
  created_at: string
  created_by: string
  /** False for a version that was uploaded — it has no structure to compare. */
  editable: boolean
}

export interface ControlSection {
  enabled: boolean
  title: string
  position: 'top' | 'after_title' | 'none'
  layout: 'table' | 'list'
  columns: number
  show_revision_history: boolean
  revision_history_title: string
  rows: ControlRow[]
}

export type ListNumbering =
  'decimal' | 'legal' | 'alpha' | 'roman' | 'alphaUpper' | 'romanUpper'

export interface DocumentTemplate {
  key: string
  name: string
  description: string
  /** `enforce` locks formatting to the template; `override` lets a user change it. */
  mode: 'override' | 'enforce'
  font_family: string
  font_size: number
  page_layout: PageLayout
  list_numbering: ListNumbering
  rules: Record<string, FormattingRule>
  control_section: ControlSection
  starter: JSONContent[]
}

/** What `/authoring/new` and `/authoring/open` return. */
export interface AuthoringSession {
  document_id: string
  version_id: string
  title: string
  doctype_id: string
  /** The folder it is filed in — the default destination for a copy. */
  folder_id?: string
  doctype: { id: string; type_key?: string; title?: string }
  authoring: { mode: 'off' | 'optional' | 'required'; template: string }
  template: DocumentTemplate
  content: JSONContent
  /**
   * Set only when this version was UPLOADED and is being opened for the first
   * time: the original converted to HTML, for the editor to parse through its
   * own schema. `content` is empty then — the server never builds editor JSON,
   * because the schema that decides what a document may contain lives here.
   */
  import_html?: string
  page_layout: PageLayout
  control: ResolvedControlRow[]
  /**
   * The version label a save will CREATE, per kind of save.
   *
   * `control` above describes the version being OPENED, because that is the
   * only version record that exists while the author is typing. Filing the
   * PDF rendered from those rows is what made every document name the
   * revision before it — 3.1 printing "2.1". The editor swaps this in when it
   * renders the copy it is about to file; the server works it out with the
   * same rule that will number the row (`store.next_version_numbers`).
   */
  next_version?: { minor: string; major: string }
  revision_history: RevisionRow[]
  versions: VersionChoice[]
  state?: string
  checked_out_by?: string
  can_edit: boolean
}

export interface AuthoringCompareSide {
  version_id: string
  label: string
  created_at: string
  created_by: string
  content: JSONContent
  template: DocumentTemplate
}

export interface AuthoringComparison {
  document_id: string
  title: string
  a: AuthoringCompareSide
  b: AuthoringCompareSide
}

/**
 * The transport the host injects, exactly as `DmsClient` is injected — this
 * package renders and lays out; it never decides where the API lives.
 */
export interface AuthoringClient {
  templates(): Promise<{
    templates: DocumentTemplate[]
    elements: Array<{ id: string; label: string; structural: boolean }>
    document_keys: string[]
    list_numbering: string[]
    /** The Import menu is built from this, so it cannot offer what the server
     *  has no converter for. Absent on a server that predates importing. */
    import_formats?: ImportFormat[]
  }>
  newDocument(doctypeId: string): Promise<AuthoringSession>
  open(documentId: string, versionId?: string): Promise<AuthoringSession>
  save(input: {
    content: JSONContent
    pdf: Blob
    title?: string
    documentId?: string
    doctypeId?: string
    folderId?: string
    template?: DocumentTemplate
    templateKey?: string
    pageLayout?: PageLayout
    major?: boolean
    metadata?: Record<string, unknown>
  }): Promise<{
    document_id: string
    version_id: string
    page_count: number
    size_bytes: number
    title: string
  }>
  compare(documentId: string, a: string, b: string): Promise<AuthoringComparison>
  /**
   * Where a COPY may be filed: the types that can be written here, and the
   * folders it can go in.
   *
   * Optional. A host that does not implement it simply never offers "save as a
   * new document" — the alternative is an editor that offers to file a copy
   * somewhere it cannot ask about, and finds out at the save.
   */
  choices?(): Promise<AuthoringChoices>
  /**
   * Convert a Word / OpenDocument / HTML / text file to HTML the editor can
   * parse. Optional, like `choices`: a host that does not serve the route
   * simply never offers Import.
   */
  importFile?(file: File): Promise<AuthoringImport>
}

/** What a converted document comes back as. */
export interface AuthoringImport {
  /**
   * HTML, NOT editor JSON.
   *
   * The editor parses it through its own schema, which is what decides what a
   * Twasta document may contain — so anything the schema does not recognise is
   * dropped on the way in. A server that sent JSON would be a second, silent
   * definition of the schema.
   */
  html: string
  format: string
  /** What did not survive the conversion. Shown to the author, never swallowed. */
  warnings: string[]
}

/** One format the Import menu offers, as the server declares it. */
export interface ImportFormat {
  id: string
  label: string
  extensions: string[]
  /** For the file picker's `accept`. */
  accept: string
  hint: string
}

/** The destinations "save as a new document" can offer. */
export interface AuthoringChoices {
  doctypes: Array<{
    id: string
    title: string
    /** `off` types are shown disabled with the reason: they are upload-only. */
    authoring_mode: string
  }>
  folders: Array<{ id: string; name: string; parent_id: string }>
}
